import api from '../../services/api';

/**
 * API client for user-managed customer categories ("Kategoria": Indywidualny,
 * Dystrybucje, Sieci handlowe, Prefabrykacje końcowe). Mirrors
 * ../products/productCategoriesApi.ts.
 */

export interface CustomerCategory {
  id: string;
  name: string;
  description?: string | null;
  color?: string | null;
  customerCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface CustomerCategoryPayload {
  name: string;
  description?: string | null;
  color?: string | null;
}

export interface CustomerCategoryAssignedCustomer {
  id: string;
  name: string;
  email?: string | null;
  taxId?: string | null;
  address?: string | null;
  customerCategoryId?: string | null;
  customerCategoryName?: string | null;
  customerCategoryColor?: string | null;
}

// Single home for mapping a backend category error to a translated (Polish-first)
// message. The backend sends a stable `code`; the English `message` is never
// shown to the user. Unknown/uncoded errors fall back to the caller's string.
export function categoryErrorMessage(
  error: any,
  t: (key: string) => string,
  fallback: string
): string {
  const code = error?.response?.data?.code;
  if (code === 'DUPLICATE_CATEGORY_NAME') {
    return t('customers:customerCategory.duplicateName');
  }
  if (code === 'STALE_CUSTOMER_CATEGORY') {
    return t('customers:customerCategory.staleCategory');
  }
  if (code === 'STALE_CUSTOMER_SELECTION') {
    return t('customers:customerCategory.staleCustomerSelection');
  }
  return fallback;
}

export async function fetchCustomerCategoryTags() {
  const response = await api.get<CustomerCategory[]>('/customer-categories');
  return response.data;
}

export async function createCustomerCategoryTag(payload: CustomerCategoryPayload) {
  const response = await api.post<CustomerCategory>('/customer-categories', payload);
  return response.data;
}

export async function updateCustomerCategoryTag(id: string, payload: CustomerCategoryPayload) {
  const response = await api.put<CustomerCategory>(`/customer-categories/${id}`, payload);
  return response.data;
}

export async function deleteCustomerCategoryTag(id: string) {
  await api.delete(`/customer-categories/${id}`);
}

export async function fetchCustomerCategoryAssignments(id: string) {
  const response = await api.get<CustomerCategoryAssignedCustomer[]>(`/customer-categories/${id}/customers`);
  return response.data;
}

export async function fetchCustomerCategoryAssignmentCandidates() {
  const response = await api.get<CustomerCategoryAssignedCustomer[]>('/customer-categories/assignment-candidates');
  return response.data;
}

export async function replaceCustomerCategoryAssignments(id: string, customerIds: string[]) {
  const response = await api.put<CustomerCategory>(`/customer-categories/${id}/customers`, { customerIds });
  return response.data;
}
